import React, { useState, useContext, useEffect } from 'react';
import has from 'lodash/has';
import produce from 'immer';
import { useDispatch } from 'react-redux';

import NODE_TYPES from 'src/models/Ivrs/NodeTypes';
import SelectNodeTypeModal from 'src/presentation/components/pages/ivr/components/SelectNodeTypeModal';
import ivrsResource from 'src/actions/ivrs/ivrsResource';
import { IvrEditorLocationStateContext, IvrEditorContext } from 'src/presentation/components/pages/ivr/IvrEditor';
import { IvrNodePosition } from 'src/models/Ivrs/IvrTreeData';
import { START_NODE_ID } from 'src/presentation/components/pages/ivr/constants/ivrTreeConstants';
import { calculateNodePosition } from 'src/presentation/components/pages/ivr/components/ivrTree/helpers/nodesPositionHelper';
import { getNewNodeBaseOnType } from 'src/presentation/components/pages/ivr/components/ivrTree/helpers/newNodesHelper';

export enum NewNodeType {
  TEMPLATE,
  NODE,
}

const useCreateNewNode = ({
  attr,
  onBlur,
  workingState,
  nodeList,
  stayOnParentNodeAfterCreation,
  onSelectNode,
  onChange,
  limitNodeTypesTo,
}) => {
  const [addTemplate, setAddTemplate] = useState<any>();
  const [showSelectNodeTypeModal, setShowSelectNodeTypeModal] = useState(false);

  const { locationState, setLocationState } = useContext(IvrEditorLocationStateContext);
  const { currentComponentId, setIsSaveInProgress } = useContext(IvrEditorContext);

  const dispatch = useDispatch();

  const parentId = has(workingState, `nodesMap.${currentComponentId}`) ? currentComponentId : START_NODE_ID;

  useEffect(() => {
    if (addTemplate) {
      setAddTemplate(null);
      if (!stayOnParentNodeAfterCreation) {
        setIsSaveInProgress(true);
        onBlur && onBlur({ currentState: addTemplate });
        return;
      }
      onChange && onChange(addTemplate);
    }
  }, [addTemplate, onBlur, onChange, onSelectNode, setAddTemplate, setIsSaveInProgress, stayOnParentNodeAfterCreation]);

  const selectTemplate = templateId => {
    if (!stayOnParentNodeAfterCreation) {
      setIsSaveInProgress(true);
      setShowSelectNodeTypeModal(false);
      dispatch(ivrsResource.effects.addTreeFromTemplateToConfiguration({ templateId, attr, parentId }));
      return;
    }
    setShowSelectNodeTypeModal(false);
    setAddTemplate({ type: NewNodeType.TEMPLATE, templateId });
  };

  const selectNodeType = (nodeType: NODE_TYPES) => {
    if (!stayOnParentNodeAfterCreation) {
      setIsSaveInProgress(true);
      dispatch(ivrsResource.effects.addNodesToConfiguration({ attr, parentId, nodeType }));
      setShowSelectNodeTypeModal(false);
      return;
    }

    const newNode = getNewNodeBaseOnType({ nodeType, nodeList });
    setShowSelectNodeTypeModal(false);

    setNewNodePosition(newNode.ivrNode.id);
    setAddTemplate({ type: NewNodeType.NODE, node: newNode });
  };

  const setNewNodePosition = newId => {
    const nodePosition = calculateNodePosition({
      parentId: parentId || START_NODE_ID,
      locationState: locationState as IvrNodePosition,
    });
    const newLocationState = produce<IvrNodePosition>(locationState as IvrNodePosition, (draft: any) => {
      draft[newId] = nodePosition;
    });

    setLocationState(newLocationState);
  };

  const selectNodeTypeModal = showSelectNodeTypeModal ? (
    <SelectNodeTypeModal
      onSelectType={selectNodeType}
      onSelectTemplate={selectTemplate}
      onClose={() => setShowSelectNodeTypeModal(false)}
      limitNodeTypesTo={limitNodeTypesTo}
    />
  ) : null;

  return { selectNodeTypeModal, setShowSelectNodeTypeModal };
};

export default useCreateNewNode;
